import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Ripple Effect Button Component
export const RippleButton = ({ 
  children, 
  onClick, 
  className = '', 
  disabled = false,
  variant = 'primary', // primary, secondary, ghost, danger
  size = 'md', // sm, md, lg
  ...props 
}) => {
  const [ripples, setRipples] = useState([]);

  const addRipple = (e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    
    const newRipple = {
      x,
      y,
      size,
      id: Date.now()
    };
    
    setRipples(prev => [...prev, newRipple]);
    
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== newRipple.id));
    }, 600);
  };

  const handleClick = (e) => {
    if (!disabled) {
      addRipple(e);
      onClick?.(e);
    }
  };

  const variants = {
    primary: 'bg-[#00F0FF] text-black hover:shadow-[0_0_30px_rgba(0,240,255,0.4)]',
    secondary: 'bg-[#0F0F16] text-white border border-white/10 hover:border-[#00F0FF]/30',
    ghost: 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5',
    danger: 'bg-[#FF2A2A]/10 text-[#FF2A2A] hover:bg-[#FF2A2A]/20',
    success: 'bg-[#00FF66]/10 text-[#00FF66] hover:bg-[#00FF66]/20',
    warning: 'bg-[#FFE800]/10 text-[#FFE800] hover:bg-[#FFE800]/20'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm rounded-lg',
    md: 'px-4 py-2.5 text-base rounded-xl',
    lg: 'px-6 py-3.5 text-lg rounded-2xl'
  };

  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      onClick={handleClick}
      disabled={disabled}
      className={`
        relative overflow-hidden font-medium transition-all duration-300
        ${variants[variant]}
        ${sizes[size]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      {...props}
    >
      {/* Ripple effects */}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute bg-white/30 rounded-full animate-ripple pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: ripple.size,
            height: ripple.size
          }}
        />
      ))}
      
      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
    </motion.button>
  );
};

// Glowing Icon Button
export const GlowButton = ({ 
  children, 
  onClick, 
  className = '', 
  color = '#00F0FF',
  active = false,
  ...props 
}) => {
  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative p-3 rounded-xl transition-all duration-300
        ${active ? 'bg-white/10' : 'bg-[#08080C] hover:bg-[#0F0F16]'}
        ${className}
      `}
      style={{
        boxShadow: active ? `0 0 20px ${color}40` : 'none'
      }}
      {...props}
    >
      <span style={{ color: active ? color : '#9CA3AF' }}>
        {children}
      </span>
    </motion.button>
  );
};

// Pulse Button (for notifications, live indicators)
export const PulseButton = ({ children, onClick, className = '', pulse = true, ...props }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`relative ${className}`}
      {...props}
    >
      {pulse && (
        <>
          <span className="absolute inset-0 rounded-full bg-[#FF2A2A] animate-ping opacity-30" />
          <span className="absolute inset-0 rounded-full bg-[#FF2A2A] animate-pulse opacity-50" />
        </>
      )}
      <span className="relative z-10">{children}</span>
    </motion.button>
  );
};

// Neon Border Button
export const NeonButton = ({ 
  children, 
  onClick, 
  className = '',
  color = '#00F0FF',
  ...props 
}) => {
  return (
    <motion.button
      whileHover={{ 
        scale: 1.02,
        boxShadow: `0 0 30px ${color}40, inset 0 0 30px ${color}10`
      }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`
        relative px-6 py-3 rounded-xl font-semibold
        bg-transparent border-2 transition-all duration-300
        ${className}
      `}
      style={{
        borderColor: color,
        color: color,
        textShadow: `0 0 10px ${color}60`
      }}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export default RippleButton;
